import { DollarOutlined } from "@ant-design/icons";
import { Button, Checkbox, message, Modal, Radio, Select, Space, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../server";
import dayjs from "dayjs";
import Client from "../clients/client";
import { cnvString2Time, RECORD_PASS_VID, resolveRecordPassVid } from "../../extensions/utils";
import "./records.css";

const mapRecord = (data) => {
  const start = cnvString2Time(data.start);
  const finish = cnvString2Time(data.finish);
  const base = data.docDate ? dayjs(data.docDate) : dayjs();
  return {
    ...data,
    start: start
      ? base.hour(start.hour()).minute(start.minute()).second(start.second())
      : null,
    finish: finish
      ? base.hour(finish.hour()).minute(finish.minute()).second(finish.second())
      : null,
  };
};

const deriveMode = (record) => {
  const clients = record.cnts
    .filter((c) => c.objectVid === 1)
    .map((c) => ({
      ...c,
      effectivePassVid: resolveRecordPassVid(c.passVid, record.passVid),
    }));

  // Проверяем наличие клиентов с каждым типом по очереди
  if (clients.some(c => c.effectivePassVid === RECORD_PASS_VID.ByAbon)) {
    return "abon";
  }
  if (clients.some(c => c.effectivePassVid === RECORD_PASS_VID.ByPayment)) {
    return "paid";
  }
  if (clients.some(c => c.effectivePassVid === RECORD_PASS_VID.ByPacket)) {
    return "packet";
  }
  return "paid";
};

/** Оформление визита по записи */
const RecordVisit = ({ id, onClose, onRefresh }) => {
  const [item, setItem] = useState(null);
  const [mode, setMode] = useState("paid");
  const [paidSelectedIds, setPaidSelectedIds] = useState([]);
  const [selectedClientCntId, setSelectedClientCntId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abon, setAbon] = useState(null);
  const [abons, setAbons] = useState([]);
  /** { id: objectId клиента, name: objectName } при открытой форме клиента */
  const [editClient, setEditClient] = useState(null);
  const [packetSelectedClientCntId, setPacketSelectedClientCntId] = useState(null);
  const [packetAbons, setPacketAbons] = useState([]);
  const [packetAbon, setPacketAbon] = useState(null);

  const loadItem = useCallback(() => {
    setLoading(true);
    serverFetch(`record/${id}`)
      .then((data) => {
        const mapped = mapRecord(data);
        setItem(mapped);
        setMode(deriveMode(mapped));
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  }, [id]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const allClients = useMemo(
    () =>
      (item?.cnts ?? [])
        .filter((c) => c.objectVid === 1)
        .map((c) => ({
          ...c,
          effectivePassVid: resolveRecordPassVid(c.passVid, item.passVid),
        })),
    [item]
  );

  const paidClients = useMemo(
    () => allClients.filter((c) => c.effectivePassVid === RECORD_PASS_VID.ByPayment),
    [allClients]
  );
  const abonClients = useMemo(
    () => allClients.filter((c) => c.effectivePassVid === RECORD_PASS_VID.ByAbon),
    [allClients]
  );
  const packetClients = useMemo(
    () => allClients.filter((c) => c.effectivePassVid === RECORD_PASS_VID.ByPacket),
    [allClients]
  );

  useEffect(() => {
    if (!item) return;

    // Проверяем, все ли клиенты имеют одинаковый effectivePassVid
    // Если есть клиенты с разными видами прохода, оставляем выбор в покое
    if ([...new Set(allClients.map(c => c.effectivePassVid))].length !== 1) return;
    
    // Если все одного типа, действуем по текущему mode
    if (mode === "paid") {
      setPaidSelectedIds((prev) => {
        const unusedIds = paidClients.filter((i) => !i.used).map((i) => i.id);
        if (prev.length === 0) return unusedIds;
        const kept = prev.filter((id) => unusedIds.includes(id));
        return kept.length ? kept : unusedIds;
      });
      setSelectedClientCntId(null);
      setPacketSelectedClientCntId(null);
    } else if (mode === "abon") {
      const unused = abonClients.filter((c) => !c.used);
      setSelectedClientCntId((prev) => {
        if (prev != null && unused.some((c) => c.id === prev)) return prev;
        return unused.length ? unused[0].id : null;
      });
      setPaidSelectedIds([]);
      setPacketSelectedClientCntId(null);
    } else if (mode === "packet") {
      const unused = packetClients.filter((c) => !c.used);
      setPacketSelectedClientCntId((prev) => {
        if (prev != null && unused.some((c) => c.id === prev)) return prev;
        return unused.length ? unused[0].id : null;
      });
      setPaidSelectedIds([]);
      setSelectedClientCntId(null);
    }
  }, [item, mode, paidClients, abonClients, packetClients, allClients]);

  useEffect(() => {
    if (mode !== "packet" || packetSelectedClientCntId == null) {
      setPacketAbons([]);
      setPacketAbon(null);
      return;
    }

    const clientObj = packetClients.find((c) => c.id === packetSelectedClientCntId);
    if (!clientObj) return;

    const clientId = clientObj.objectId;
    // Получаем ID продуктов (услуг) из записи
    const productIdsInRecord = (item?.cnts ?? [])
      .filter((c) => c.objectVid === 2)
      .map((c) => c.objectId);

    serverFetch(`Sale/ClientPackets/${clientId}`, { method: "GET" })
      .then((data) => {
        const activePacketSales = data.filter(
          (sale) => sale.status === 2 && !sale.deleted && !sale.blocked
        );

        if (activePacketSales.length === 0) {
          setPacketAbons([]);
          setPacketAbon(null);
          return;
        }

        const eligibleAbons = [];
        const processNext = (index) => {
          if (index >= activePacketSales.length) {
            setPacketAbons(eligibleAbons);
            setPacketAbon((prev) => {
              if (eligibleAbons.length === 0) return null;
              if (prev != null && eligibleAbons.some((a) => a.value === prev)) return prev;
              return eligibleAbons[0].value;
            });
            return;
          }

          const sale = activePacketSales[index];
          serverFetch(`sale/ProductList/${sale.id}`, { method: "GET" })
            .then((products) => {
              const hasProduct = products.some(
                (p) => productIdsInRecord.includes(p.productId) && (p.qtyRest || 0) > 0
              );
              if (hasProduct) {
                eligibleAbons.push({
                  label: `[${sale.id}] ${sale.productName || `Абонемент #${sale.id}`}`,
                  value: sale.id,
                });
              }
            })
            .catch(() => {}) // ошибка загрузки пакетов для одного абонемента - игнорируем
            .finally(() => processNext(index + 1));
        };

        processNext(0);
      })
      .catch((e) => {
        message.error(e.userMessage);
        setPacketAbons([]);
        setPacketAbon(null);
      });
  }, [mode, packetSelectedClientCntId, packetClients, item]);

  const selectedRow = useMemo(
    () => abonClients.find((c) => c.id === selectedClientCntId),
    [abonClients, selectedClientCntId]
  );
  const selectedClientObjectId = selectedRow?.objectId;

  const loadAbons = useCallback(() => {
    if (mode !== "abon" || selectedClientObjectId == null) {
      setAbons([]);
      setAbon(null);
      return;
    }
    serverFetch(`sale/ListByClient/${selectedClientObjectId}?vids=2`)
      .then((data) => {
        const newAbons = data
          .filter((i) => i.status === 2 && !i.deleted)
          .map((i) => ({ label: `[${i.id}] ${i.productName}`, value: i.id }));
        setAbons(newAbons);
        setAbon((prev) => {
          if (newAbons.length === 0) return null;
          if (prev != null && newAbons.some((a) => a.value === prev)) return prev;
          return newAbons[0].value;
        });
      })
      .catch((e) => message.error(e.userMessage));
  }, [mode, selectedClientObjectId]);

  useEffect(() => {
    loadAbons();
  }, [loadAbons]);

  const loadPacketAbons = useCallback(() => {
    if (mode !== "packet" || packetSelectedClientCntId == null) return;
    const clientObj = packetClients.find(c => c.id === packetSelectedClientCntId);
    if (!clientObj) return;

    const clientId = clientObj.objectId;
    const productIdsInRecord = (item?.cnts ?? [])
      .filter(c => c.objectVid === 2)
      .map(c => c.objectId);

    serverFetch(`Sale/ClientPackets/${clientId}`, { method: "GET" })
      .then(data => {
        const activePacketSales = data.filter(sale => sale.status === 2 && !sale.deleted && !sale.blocked);
        if (activePacketSales.length === 0) {
          setPacketAbons([]);
          setPacketAbon(null);
          return;
        }
        const eligibleAbons = [];
        const processNext = (index) => {
          if (index >= activePacketSales.length) {
            setPacketAbons(eligibleAbons);
            setPacketAbon(prev => {
              if (eligibleAbons.length === 0) return null;
              if (prev != null && eligibleAbons.some(a => a.value === prev)) return prev;
              return eligibleAbons[0].value;
            });
            return;
          }
          const sale = activePacketSales[index];
          serverFetch(`sale/ProductList/${sale.id}`, { method: "GET" })
            .then(products => {
              const hasProduct = products.some(p => productIdsInRecord.includes(p.productId) && (p.qtyRest || 0) > 0);
              if (hasProduct) {
                eligibleAbons.push({ label: `[${sale.id}] ${sale.productName || `Абонемент #${sale.id}`}`, value: sale.id });
              }
            })
            .catch(() => {})
            .finally(() => processNext(index + 1));
        };
        processNext(0);
      })
      .catch(e => {
        message.error(e.userMessage);
        setPacketAbons([]);
        setPacketAbon(null);
      });
  }, [mode, packetSelectedClientCntId, packetClients, item]);

  const handleCloseClientForm = useCallback(() => {
    setEditClient(null);
    if (mode === "abon") {
      loadAbons();
    } else if (mode === "packet") {
      loadPacketAbons();
    }
  }, [mode, loadAbons, loadPacketAbons]);

  const switchToPaid = () => {
    setMode("paid");
  };

  const switchToAbon = (clientCntId) => {
    setMode("abon");
    if (clientCntId != null) setSelectedClientCntId(clientCntId);
  };

  const togglePaidClient = (cntId, checked) => {
    if (mode !== "paid") switchToPaid();
    setPaidSelectedIds((prev) =>
      checked ? [...prev, cntId] : prev.filter((x) => x !== cntId)
    );
  };

  const passVidHint = (effectivePassVid) => {
    if (effectivePassVid === RECORD_PASS_VID.ByPayment) {
      return (
        <Tooltip title="За плату">
          <DollarOutlined style={{ marginLeft: 4, color: "#2f6f00" }} />
        </Tooltip>
      );
    }
    if (effectivePassVid === RECORD_PASS_VID.ByPacket) {
      return <span className="record-visit-pass-hint">пакет</span>;
    }
    return <span className="record-visit-pass-hint">абонемент</span>;
  };

  const divClientsPaid = paidClients.map((c) => (
    <div className="record-visit-item" key={c.id}>
      <Space style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Checkbox
          checked={mode === "paid" && paidSelectedIds.includes(c.id)}
          disabled={c.used}
          onChange={(e) => togglePaidClient(c.id, e.target.checked)}
        >
          {c.objectName}
          {passVidHint(c.effectivePassVid)}
        </Checkbox>
        <Button onClick={() => setEditClient({ id: c.objectId, name: c.objectName })}>
          Изменить
        </Button>
      </Space>
    </div>
  ));

  const divClientsAbon = (
    <Radio.Group
      className="record-visit-client-radios"
      value={mode === "abon" ? selectedClientCntId ?? null : null}
      onChange={(e) => switchToAbon(e.target.value)}
    >
      {abonClients.map((c) => (
        <div className="record-visit-item" key={c.id}>
          <Space style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Radio value={c.id} disabled={c.used}>
              {c.objectName}
              {passVidHint(c.effectivePassVid)}
            </Radio>
            <Button onClick={() => setEditClient({ id: c.objectId, name: c.objectName })}>
              Изменить
            </Button>
          </Space>
        </div>
      ))}
    </Radio.Group>
  );

  const divClientsPackets = (
    <Radio.Group
      className="record-visit-client-radios"
      value={mode === "packet" ? packetSelectedClientCntId ?? null : null}
      onChange={(e) => {
        setMode("packet");
        setPacketSelectedClientCntId(e.target.value);
      }}
    >
      {packetClients.map((c) => (
        <div className="record-visit-item" key={c.id}>
          <Space style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Radio value={c.id} disabled={c.used}>
              {c.objectName}
              {passVidHint(c.effectivePassVid)}
            </Radio>
            <Button onClick={() => setEditClient({ id: c.objectId, name: c.objectName })}>
              Изменить
            </Button>
          </Space>
        </div>
      ))}
    </Radio.Group>
  );

  const divTovs = (item?.cnts ?? [])
    .filter((i) => i.objectVid === 2)
    .map((i) => (
      <div className="record-visit-item" key={i.id}>
        {i.objectName}
      </div>
    ));

  const canOpenVisit =
    (mode === "paid" && paidSelectedIds.length > 0) ||
    (mode === "abon" && selectedClientCntId != null && abon != null && abons.length > 0) ||
    (mode === "packet" && packetSelectedClientCntId != null && packetAbon != null && packetAbons.length > 0);

  const onSave = () => {
    if (!canOpenVisit) return;
    setLoading(true);
    let bodyData;
    if (mode === "paid") {
      bodyData = { recordCnts: paidSelectedIds, saleId: null };
    } else if (mode === "abon") {
      bodyData = { recordCnts: [selectedClientCntId], saleId: abon };
    } else if (mode === "packet") {
      bodyData = { recordCnts: [packetSelectedClientCntId], saleId: packetAbon };
    }
    serverFetch("visit/AddByRecords", {
      method: "POST",
      bodyData
    })
      .then(() => onClose(true))
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
        loadItem();
        onRefresh?.();
        if (mode === "abon") {
          loadAbons();
        } else if (mode === "packet") {
          loadPacketAbons();
        }
      });
  };

  const timeWarning = useMemo(() => {
    if (!item?.start || !item?.finish) return null;
    const now = dayjs();
    if (now.isBefore(item.start)) return "Время записи еще не наступило";
    if (now.isAfter(item.finish)) return "Время записи уже прошло";
    return null;
  }, [item]);

  const selectionMixed = paidClients.length > 0 && (abonClients.length > 0 || packetClients.length > 0);

  return (
    <Modal open onCancel={() => onClose()} footer={null} title={`Визит по записи # ${id}`} loading={loading || !item}>
      {item && (
        <>
          {paidClients.length > 0 && (
            <>
              <div className="record-visit-title">
                {paidClients.length === 1 && abonClients.length === 0 && packetClients.length === 0 ? "Клиент" : "Клиенты (за плату)"}
              </div>
              {divClientsPaid}
            </>
          )}
          {abonClients.length > 0 && (
            <>
              <div className="record-visit-title">
                {abonClients.length === 1 && paidClients.length === 0 && packetClients.length === 0 ? "Клиент" : "Клиенты (по абонементу)"}
              </div>
              {divClientsAbon}
            </>
          )}
          {packetClients.length > 0 && (
            <>
              <div className="record-visit-title">
                {packetClients.length === 1 && paidClients.length === 0 && abonClients.length === 0 ? "Клиент" : "Клиенты (за пакет)"}
              </div>
              {divClientsPackets}
            </>
          )}
          {selectionMixed && (
            <div className="record-visit-hint">
              По записи можно открыть один визит с видом либо за плату, либо за абонемент, либо за услуги пакета
            </div>
          )}
          {mode === "abon" && (
            <>
              <div className="record-visit-title">Абонемент</div>
              {!selectedClientCntId ? (
                <div className="record-visit-item record-visit-hint">Выберите клиента</div>
              ) : (
                <div className="record-visit-item">
                  <Select className="record-visit-select" options={abons} value={abon} onChange={setAbon} />
                </div>
              )}
            </>
          )}
          {mode === "packet" && (
            <>
              <div className="record-visit-title">Абонемент с пакетами</div>
              {!packetSelectedClientCntId ? (
                <div className="record-visit-item record-visit-hint">Выберите клиента</div>
              ) : packetAbons.length === 0 ? (
                <div className="record-visit-item record-visit-hint">Нет подходящих абонементов с пакетами</div>
              ) : (
                <div className="record-visit-item">
                  <Select
                    className="record-visit-select"
                    options={packetAbons}
                    value={packetAbon}
                    onChange={setPacketAbon}
                    placeholder="Выберите абонемент"
                  />
                </div>
              )}
            </>
          )}
          <div className="record-visit-title">Услуги</div>
          {divTovs}
          {timeWarning && <div className="record-visit-time-warning">{timeWarning}</div>}
          <div className="record-visit-footer">
            <Button disabled={!canOpenVisit} className="dolg-button" onClick={onSave}>
              Открыть визит
            </Button>
          </div>
        </>
      )}
      {editClient != null && (
        <div className="record-visit-client-modal-front">
          <Client
            id={editClient.id}
            name={editClient.name}
            onClose={handleCloseClientForm}
          />
        </div>
      )}
    </Modal>
  );
};

export default RecordVisit;
